import React, { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Grid,
  Button,
  Pagination,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Loyalty as LoyaltyIcon,
  ShoppingBag as OrdersIcon,
  Payment as PaymentIcon,
  Notes as NotesIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as DateIcon,
  CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { fetchUserDetails } from '../api/superAdmin';
import CustomerAddressEditor from './CustomerAddressEditor';
import StoreCreditPanel from './StoreCreditPanel';

// Tab index for the Store Credit tab — exported so callers can open the modal
// straight to it (e.g. the Users-page store-credit shortcut).
export const STORE_CREDIT_TAB = 2;

// Define a User type
interface User {
  id: number;
  email: string;
  password?: string;
  role: 'super_admin' | 'location_admin' | 'customer';
  name: string;
  phone?: string;
  location_name?: string | null;
  location_id?: number | null;
}

// User Details Modal Component
type UserDetailsModalProps = {
  userId: number | null;
  open: boolean;
  onClose: () => void;
  initialTab?: number;
};

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ userId, open, onClose, initialTab = 0 }) => {
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  // pagination states
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);
  const [txPage, setTxPage] = useState(1);
  const [txTotalPages, setTxTotalPages] = useState(1);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [refreshKey, setRefreshKey] = useState(0);
  const reload = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    if (userId && open) {
      setLoading(true);
      fetchUserDetails(userId, {
        orders_page: ordersPage,
        orders_limit: 10,
        tx_page: txPage,
        tx_limit: 10,
      })
        .then((res) => {
          setData(res);
          // Calculate total pages based on backend data
          if (res.orders) {
            const totalOrders = res.orders.summary.order_count;
            setOrdersTotalPages(Math.ceil(totalOrders / 10));
          }
          if (res.transactions) {
  const totalTx = res.transactions.summary.total_count || 0;
  setTxTotalPages(Math.max(1, Math.ceil(totalTx / 10)));
}


        })
        .finally(() => setLoading(false));
    }
  }, [userId, open, ordersPage, txPage, refreshKey]);

  // When the modal is opened, land on the requested tab.
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  const handleClose = () => {
    onClose();
    setTab(0);
    setOrdersPage(1);
    setTxPage(1);
  };

  const handleOrdersPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setOrdersPage(value);
  };

  const handleTxPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setTxPage(value);
  };

  const TabPanel = ({ children, value, index }: any) => (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: isMobile ? 0 : 2,
          background: (theme) => `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="bold" color="primary">
          User Details
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Divider />
      
      <Box sx={{ px: 2, pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <>
            <Tabs 
              value={tab} 
              onChange={(_, v) => setTab(v)}
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': { minWidth: 'auto', px: 1.5 },
                '& .Mui-selected': { color: 'primary.main', fontWeight: 'bold' },
              }}
            >
              <Tab icon={<PersonIcon />} iconPosition="start" label="Details" />
              <Tab icon={<LoyaltyIcon />} iconPosition="start" label="Loyalty" />
              <Tab icon={<CreditCardIcon />} iconPosition="start" label="Store Credit" />
              <Tab icon={<OrdersIcon />} iconPosition="start" label="Orders" />
              <Tab icon={<PaymentIcon />} iconPosition="start" label="Transactions" />
              <Tab icon={<NotesIcon />} iconPosition="start" label="Notes" />
            </Tabs>

            <TabPanel value={tab} index={0}>
              <Card sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1 }} /> Personal Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2"><b>Name:</b> {data.user.name}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2"><b>Email:</b> {data.user.email}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PhoneIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2"><b>Phone:</b> {data.user.phone || 'N/A'}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2"><b>Location:</b> {data.user.location_name || 'N/A'}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 2, mb : 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocationIcon sx={{ mr: 1 }} /> Addresses
                  </Typography>
                  {userId && (
                    <CustomerAddressEditor
                      userId={userId}
                      addresses={data.addresses || []}
                      onChanged={reload}
                    />
                  )}
                </CardContent>
              </Card>
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Card sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <LoyaltyIcon sx={{ mr: 1 }} /> Loyalty Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ textAlign: 'center', p: 1, borderRadius: 2 }}>
                        <Typography variant="h6" color="primary">{data.loyalty.balance}</Typography>
                        <Typography variant="caption">Balance</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ textAlign: 'center', p: 1, borderRadius: 2 }}>
                        <Typography variant="h6" color="success.main">{data.loyalty.total_earned}</Typography>
                        <Typography variant="caption">Total Earned</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ textAlign: 'center', p: 1, borderRadius: 2 }}>
                        <Typography variant="h6" color="error.main">{data.loyalty.total_spent}</Typography>
                        <Typography variant="caption">Total Spent</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 2, mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Recent Transactions
                  </Typography>
                  {isMobile ? (
                    data.loyalty.transactions.map((tx: any) => (
                      <Card key={tx.id} variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {tx.transaction_type}
                            </Typography>
                            <Chip 
                              label={tx.amount} 
                              size="small" 
                              color={tx.amount.startsWith('-') ? "error" : "success"}
                              variant="outlined"
                            />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {tx.description}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <DateIcon sx={{ fontSize: 14, mr: 0.5 }} />
                            <Typography variant="caption">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.loyalty.transactions.map((tx: any) => (
                          <TableRow key={tx.id}>
                            <TableCell>{new Date(tx.created_at).toLocaleDateString()}</TableCell>
                            <TableCell>{tx.transaction_type}</TableCell>
                            <TableCell>{tx.amount}</TableCell>
                            <TableCell>{tx.description}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <Card sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CreditCardIcon sx={{ mr: 1 }} /> Store Credit
                  </Typography>
                  <StoreCreditPanel customerId={userId} customerName={data.user.name} />
                </CardContent>
              </Card>
            </TabPanel>

            <TabPanel value={tab} index={3}>
              <Card sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <OrdersIcon sx={{ mr: 1 }} /> Order Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ textAlign: 'center', p: 1, borderRadius: 2 }}>
                        <Typography variant="h6" color="primary">{data.orders.summary.lifetime_spent}</Typography>
                        <Typography variant="caption">Lifetime Spent</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ textAlign: 'center', p: 1, borderRadius: 2 }}>
                        <Typography variant="h6" color="primary">{data.orders.summary.spent_last_12m}</Typography>
                        <Typography variant="caption">Last 12 Months</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card variant="outlined" sx={{ textAlign: 'center', p: 1, borderRadius: 2 }}>
                        <Typography variant="h6" color="primary">{data.orders.summary.order_count}</Typography>
                        <Typography variant="caption">Order Count</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 2, mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Order History
                  </Typography>
                  {isMobile ? (
                    data.orders.list.map((order: any) => (
                      <Card key={order.id} variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight="bold">
                              Order #{order.order_number}
                            </Typography>
                            <Chip 
                              label={order.total} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Chip 
                              label={order.payment_status} 
                              size="small" 
                              color={order.payment_status === 'paid' ? "success" : "default"}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <DateIcon sx={{ fontSize: 14, mr: 0.5 }} />
                              <Typography variant="caption">
                                {new Date(order.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Order #</TableCell>
                          <TableCell>Total</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.orders.list.map((order: any) => (
                          <TableRow key={order.id}>
                            <TableCell>{order.order_number}</TableCell>
                            <TableCell>{order.total}</TableCell>
                            <TableCell>{order.payment_status}</TableCell>
                            <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  
                  {/* Orders Pagination */}
                  {ordersTotalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={ordersTotalPages}
                        page={ordersPage}
                        onChange={handleOrdersPageChange}
                        color="primary"
                        size={isMobile ? "small" : "medium"}
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </TabPanel>

            <TabPanel value={tab} index={4}>
              <Card sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <PaymentIcon sx={{ mr: 1 }} /> Transaction Summary
                  </Typography>
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h4" color="primary">
                      {data.transactions.summary.total_paid}
                    </Typography>
                    <Typography variant="body2">Total Paid</Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 2, mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    Transaction History
                  </Typography>
                  {isMobile ? (
                    data.transactions.list.map((t: any) => (
                      <Card key={t.id} variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight="bold">
                              Order #{t.order_number}
                            </Typography>
                            <Chip 
                              label={t.amount} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Chip 
                              label={t.payment_method} 
                              size="small" 
                              variant="outlined"
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <DateIcon sx={{ fontSize: 14, mr: 0.5 }} />
                              <Typography variant="caption">
                                {new Date(t.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Order #</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.transactions.list.map((t: any) => (
                          <TableRow key={t.id}>
                            <TableCell>{t.order_number}</TableCell>
                            <TableCell>{t.amount}</TableCell>
                            <TableCell>{t.payment_method}</TableCell>
                            <TableCell>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  
                  {/* Transactions Pagination */}
                  {txTotalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={txTotalPages}
                        page={txPage}
                        onChange={handleTxPageChange}
                        color="primary"
                        size={isMobile ? "small" : "medium"}
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </TabPanel>

            <TabPanel value={tab} index={5}>
              <Card sx={{ borderRadius: 2, textAlign: 'center', py: 4, mb: 2 }}>
                <CardContent>
                  <NotesIcon color="disabled" sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Notes feature coming soon...
                  </Typography>
                </CardContent>
              </Card>
            </TabPanel>
          </>
        ) : (
          <Typography sx={{ p: 2 }}>No data available</Typography>
        )}
      </Box>
    </Dialog>
  );
};

export default UserDetailsModal;